import React, { useEffect, useState } from "react";
import api, { apiErrorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Save, RefreshCw, KeyRound, FileText } from "lucide-react";

const KOPSettings = [
  ["kop_l1", "Kop Baris 1"], ["kop_l2", "Kop Baris 2"],
  ["kategori", "Kategori (Catatan Siku)"], ["tahun_angkatan", "Tahun Angkatan"],
  ["lokasi", "Lokasi"],
  ["jabatan_penandatangan", "Jabatan Penandatangan"], ["nama_komandan", "Nama Komandan"],
  ["pangkat_komandan", "Pangkat"], ["nrp_komandan", "NRP"],
];

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [indikator, setIndikator] = useState([]);
  const [pwForm, setPwForm] = useState({ username: "", password: "" });

  const load = async () => {
    try {
      const [s, c] = await Promise.all([api.get("/admin/settings"), api.get("/constants")]);
      setSettings(s.data);
      setIndikator(c.data.indikator_observasi);
    } catch (e) { toast.error(apiErrorMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const { data } = await api.put("/admin/settings", settings);
      setSettings(data);
      toast.success("Settings disimpan");
    } catch (e) { toast.error(apiErrorMsg(e)); }
  };

  const resetDefault = async () => {
    if (!window.confirm("Reset JML OBS & SELALU 0 ke nilai default?")) return;
    const { data } = await api.get("/constants");
    const def_jml = data.indikator_observasi.map((i) => i.jml_obs);
    const def_fz_set = new Set(data.default_fixed_zero);
    const def_fz = data.indikator_observasi.map((i) => def_fz_set.has(i.no));
    setSettings({ ...settings, jml_obs: def_jml, fixed_zero: def_fz });
    toast.success("Direset ke default. Klik 'Simpan' untuk menerapkan.");
  };

  const changeAdmin = async (e) => {
    e.preventDefault();
    if (!pwForm.username && !pwForm.password) return toast.error("Isi salah satu");
    try {
      await api.put("/admin/change-credentials", pwForm);
      toast.success("Kredensial admin diperbarui. Silakan login ulang.");
      setPwForm({ username: "", password: "" });
    } catch (err) { toast.error(apiErrorMsg(err)); }
  };

  if (!settings) return <div className="p-4 md:p-8 text-slate-500">Memuat...</div>;

  return (
    <div className="p-4 md:p-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-brass-600 font-semibold mb-1">Setting</div>
        <h1 className="font-heading text-3xl font-bold text-navy-900">Pengaturan Sistem</h1>
        <p className="text-slate-500 mt-1 text-sm">Atur header dokumen, default JML OBS, dan kredensial admin.</p>
      </div>

      {/* Kop dokumen */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4">
        <h3 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2"><FileText className="w-4 h-4 text-brass-500" /> Header Dokumen</h3>
        <p className="text-xs text-slate-500 mb-4">Nama <strong>Pendidikan</strong> kini dikelola per <strong>Kelompok</strong> (menu Master Data → Kelompok). Tiap kelompok memiliki nama pendidikan sendiri yang otomatis tampil di dokumen Sosiometri, Observasi, dan Gabungan.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {KOPSettings.map(([key, label]) => (
            <div key={key}>
              <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">{label}</label>
              <input value={settings[key] || ""} onChange={(e) => setSettings({ ...settings, [key]: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            </div>
          ))}
          <div>
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Ukuran Kertas</label>
            <select value={settings.paper_size} onChange={(e) => setSettings({ ...settings, paper_size: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
              <option value="A4">A4</option>
              <option value="F4">F4/Folio</option>
            </select>
          </div>
        </div>
        <button onClick={save} data-testid="settings-save-btn" className="mt-4 bg-navy-900 hover:bg-navy-800 text-white px-5 py-2 rounded-sm text-sm font-semibold flex items-center gap-2"><Save className="w-4 h-4" /> Simpan Settings</button>
      </div>

      {/* Nilai Min/Max Scaling */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4">
        <h3 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2"><FileText className="w-4 h-4 text-brass-500" /> Skala Nilai Akhir</h3>
        <p className="text-xs text-slate-500 mb-4">Atur batas minimum & maksimum nilai akhir. Jika &quot;Aktifkan Skala&quot; dinyalakan, Nilai Siku akan dipetakan linear ke rentang [min, max] berdasar ranking (rangking 1 = nilai_max, rangking terakhir = nilai_min). Bila aktif, <strong>Nilai Siku = Nilai Akhir</strong> (nilai scaled). Bila non-aktif, Nilai Siku/Nilai Akhir mengikuti hasil rumus di bawah.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Nilai Minimum</label>
            <input type="number" step="0.01" value={settings.nilai_min ?? 75} onChange={(e) => setSettings({ ...settings, nilai_min: parseFloat(e.target.value) })} data-testid="settings-nilai-min" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono" />
          </div>
          <div>
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Nilai Maksimum</label>
            <input type="number" step="0.01" value={settings.nilai_max ?? 95} onChange={(e) => setSettings({ ...settings, nilai_max: parseFloat(e.target.value) })} data-testid="settings-nilai-max" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono" />
          </div>
          <label className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-sm cursor-pointer">
            <input type="checkbox" checked={!!settings.scale_to_range} onChange={(e) => setSettings({ ...settings, scale_to_range: e.target.checked })} data-testid="settings-scale-toggle" className="w-4 h-4 accent-brass-500" />
            <span className="text-sm font-semibold">Aktifkan Skala Linear (rangking → nilai)</span>
          </label>
        </div>
        <button onClick={save} className="mt-4 bg-navy-900 hover:bg-navy-800 text-white px-5 py-2 rounded-sm text-sm font-semibold flex items-center gap-2"><Save className="w-4 h-4" /> Simpan Skala</button>
      </div>

      {/* Fitur Tambahan */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4">
        <h3 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2"><FileText className="w-4 h-4 text-brass-500" /> Fitur Pengisian Siswa</h3>
        <p className="text-xs text-slate-500 mb-4">Atur fitur bantu pengisian sosiometri untuk Serdik.</p>
        <label className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-sm cursor-pointer">
          <input
            type="checkbox"
            checked={!!settings.auto_fill_enabled}
            onChange={(e) => setSettings({ ...settings, auto_fill_enabled: e.target.checked })}
            data-testid="settings-auto-fill-toggle"
            className="w-4 h-4 accent-brass-500"
          />
          <div>
            <div className="text-sm font-semibold text-navy-900">Aktifkan tombol &quot;Isi Otomatis&quot; pada pengisian sosiometri Serdik</div>
            <div className="text-xs text-slate-500">Jika aktif, tombol akan muncul di halaman pengisian Serdik untuk mengisi nilai 4/5 secara acak (demo/testing). Jika non-aktif, tombol disembunyikan.</div>
          </div>
        </label>
        <button onClick={save} data-testid="settings-autofill-save" className="mt-4 bg-navy-900 hover:bg-navy-800 text-white px-5 py-2 rounded-sm text-sm font-semibold flex items-center gap-2"><Save className="w-4 h-4" /> Simpan</button>
      </div>

      {/* Rumus Penilaian (Koefisien Nilai Unsur) */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4">
        <h3 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2"><FileText className="w-4 h-4 text-brass-500" /> Rumus Penilaian (Koefisien Nilai Unsur)</h3>
        <p className="text-xs text-slate-500 mb-4">
          Atur koefisien rumus perhitungan Nilai Siku. Default sesuai file Excel asli:<br />
          <span className="font-mono">Mental Rohani   = SUM(unsur 1-4)  × {(settings.koef_rohani ?? 47)}</span><br />
          <span className="font-mono">Mental Ideologi = SUM(unsur 5-6)  × {(settings.koef_ideologi ?? 91)}</span><br />
          <span className="font-mono">Mental Kejuangan= SUM(unsur 7-23) × {Number(settings.koef_kejuangan ?? 100 / 9).toFixed(4)}</span><br />
          <span className="font-mono">Nilai Siku       = (MR + MI + MK) / {(settings.divisor_sikku ?? 3)}</span>
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Koef. Mental Rohani</label>
            <input type="number" step="0.0001" value={settings.koef_rohani ?? 47} onChange={(e) => setSettings({ ...settings, koef_rohani: parseFloat(e.target.value) })} data-testid="settings-koef-rohani" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono" />
          </div>
          <div>
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Koef. Mental Ideologi</label>
            <input type="number" step="0.0001" value={settings.koef_ideologi ?? 91} onChange={(e) => setSettings({ ...settings, koef_ideologi: parseFloat(e.target.value) })} data-testid="settings-koef-ideologi" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono" />
          </div>
          <div>
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Koef. Mental Kejuangan</label>
            <input type="number" step="0.0001" value={settings.koef_kejuangan ?? (100 / 9)} onChange={(e) => setSettings({ ...settings, koef_kejuangan: parseFloat(e.target.value) })} data-testid="settings-koef-kejuangan" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono" />
          </div>
          <div>
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Divisor Nilai Siku</label>
            <input type="number" step="0.01" value={settings.divisor_sikku ?? 3} onChange={(e) => setSettings({ ...settings, divisor_sikku: parseFloat(e.target.value) })} data-testid="settings-divisor-sikku" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono" />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <button onClick={save} data-testid="settings-rumus-save" className="bg-navy-900 hover:bg-navy-800 text-white px-5 py-2 rounded-sm text-sm font-semibold flex items-center gap-2"><Save className="w-4 h-4" /> Simpan Rumus</button>
          <button
            type="button"
            onClick={() => setSettings({ ...settings, koef_rohani: 47, koef_ideologi: 91, koef_kejuangan: 100 / 9, divisor_sikku: 3 })}
            className="text-xs px-3 py-2 border border-slate-300 rounded-sm hover:bg-slate-50 flex items-center gap-1"
            data-testid="settings-rumus-reset"
          ><RefreshCw className="w-3 h-3" /> Reset ke Default Excel</button>
        </div>
      </div>

      {/* JML OBS & Fixed Zero dipindahkan ke menu Jadwal Pengisian (per-periode).
          Setiap periode menyimpan JML OBS & Toggle SELALU 0 sendiri, sesuai setingan saat jadwal dibuat. */}
      <div className="bg-slate-50 border border-dashed border-slate-300 rounded-sm p-4 mb-4 text-sm text-slate-600">
        <div className="font-semibold text-navy-900 mb-1">JML OBS & Toggle SELALU 0 (59 indikator)</div>
        Pengaturan ini kini <strong>tersimpan per Jadwal Pengisian</strong>. Buka menu <strong>Jadwal Pengisian → Detail Periode</strong> untuk mengubah nilai JML OBS &amp; SELALU 0 pada periode terkait. Nilai default diambil dari sistem saat jadwal dibuat, sehingga setiap periode dapat memiliki konfigurasi yang berbeda.
      </div>

      {/* Change admin credentials */}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <h3 className="font-heading font-bold text-navy-900 mb-4 flex items-center gap-2"><KeyRound className="w-4 h-4 text-brass-500" /> Ubah Kredensial Admin</h3>
        <form onSubmit={changeAdmin} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Username Baru</label>
            <input value={pwForm.username} onChange={(e) => setPwForm({ ...pwForm, username: e.target.value })} placeholder="Kosongkan jika tidak diubah" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>
          <div>
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Sandi Baru</label>
            <input type="password" value={pwForm.password} onChange={(e) => setPwForm({ ...pwForm, password: e.target.value })} placeholder="Kosongkan jika tidak diubah" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>
          <button type="submit" className="bg-brass-500 hover:bg-brass-600 text-white px-5 py-2 rounded-sm text-sm font-semibold">Simpan Kredensial</button>
        </form>
      </div>
    </div>
  );
}
